import React, { useEffect, useState } from 'react';
import { Box, Card, Chip, Container, Grid, IconButton, Stack, TextField } from '@mui/material';
import { dispatch, useSelector } from '../../../../redux/store';
import { PATH_DASHBOARD } from '../../../../routes/paths';
import useSettings from '../../../../hooks/useSettings';
import Page from '../../../../components/Page';
import HeaderBreadcrumbs from '../../../../components/HeaderBreadcrumbs';
import { getStocks } from '../../../../redux/slices/stockManagementRedux/stockRedux';
import { LoadingButton } from '@mui/lab';
import DeleteIcon from '@mui/icons-material/Delete';
import DataGridTable from '../../../../components/table/DataGridTable';
import { capitalize, safeJSONParse } from '../../../../utils/common';
import { addStockReturn } from '../../../../redux/slices/stockManagementRedux/stockReturnRedux';
import { toast } from 'react-toastify';
import useSound from 'use-sound';
import successFx from '../../../../assets/sounds/success.wav';
import errorFx from '../../../../assets/sounds/error.wav';

const ReturnManagement = () => {
  const { themeStretch } = useSettings();
  const [tableData, setTableData] = useState([]);
  const [data, setData] = useState({
    barcode: [],
    stockIdList: [],
  });
  const [rowSelection, setRowSelection] = useState(false);
  const { data: stocks } = useSelector((state) => state?.stock);
  const { addData: stockAddedStatus } = useSelector((state) => state?.stockReturn);
  const [initiated, setInitiated] = useState(false);

  let selectedLocation;
  ({ selectedLocation } = useSelector((state) => state.location));

  const [playErrorSound] = useSound(errorFx, {
    volume: 0.8,
    playbackRate: 1.2,
  });

  const [playSuccessSound] = useSound(successFx, {
    volume: 0.8,
    playbackRate: 1.2,
  });

  useEffect(() => {
    if (data?.barcode?.length > 0) {
      dispatch(getStocks(-1, -1, -1, data?.barcode));
    }
  }, [data?.barcode]);

  const columns = tableData?.[0]
    ? Object.keys(tableData?.[0])
        .map((value) => {
          if (value === 'status') {
            return {
              accessorKey: 'status',
              header: 'Status',
              Cell: ({ cell, row }) => (row?.original?.status === 'Eligible' ? <Chip label={'Eligible'} color={'success'} /> : <Chip label={'Not Eligible'} color={'error'} />),
            };
          }
          return {
            accessorKey: value,
            header: capitalize(value),
          };
        })
        .filter(Boolean)
    : [];

  useEffect(() => {
    const reversedData = [...data.barcode].reverse();
    setData((prevData) => ({
      ...prevData,
      stockIdList: [],
    }));
    setTableData(
      reversedData.map((barcodeIndex) => {
        const stockInfo = stocks.find((stock) => stock?.stockCode === barcodeIndex);
        const stockLocation = safeJSONParse(stockInfo?.locationInfo);
        const supplierInfo = safeJSONParse(stockInfo?.supplierInfo);
        const sameLocation = stockLocation?.locationId === selectedLocation?.id;

        setData((prevData) => ({
          ...prevData,
          stockIdList: [
            ...prevData.stockIdList,
            {
              stockId: stockInfo?.stockId,
              currentQty: stockInfo?.currentQty,
              costPerUnit: stockInfo?.costPerUnit,
            },
          ],
        }));
        return {
          barCode: barcodeIndex,
          foodInfo: stockInfo ? safeJSONParse(stockInfo.foodInfo)?.foodName : 'No Data',
          supplierName: supplierInfo?.supplierName  || '-',
          initialQty: stockInfo ? `${stockInfo.initialQty} ${safeJSONParse(stockInfo.unitInfo)?.unitName}` : '-',
          returningQty: stockInfo ? `${stockInfo.currentQty} ${safeJSONParse(stockInfo.unitInfo)?.unitName}` : '-',
          status: stockInfo?.initialQty !== stockInfo?.currentQty ||  stockInfo?.stockStatus !== 1 || !stockInfo || !sameLocation ? 'Not Eligible' : 'Eligible',
          supplierId: supplierInfo?.supplierId  || '-',
        };
      })
    );
  }, [data.barcode, stocks]);

  useEffect(() => {
    if (stockAddedStatus?.time > 0) {
      setInitiated(false);
      setData({
        barcode: [],
        stockIdList: [],
      });
      setTableData([]);
    }
  }, [stockAddedStatus]);

  const initiateTransfer = () => {
    setInitiated(true);
    if (!initiated) {
      const supplierIds = tableData?.map(value => value?.supplierId);
      const invalidBarcodes = tableData?.filter((value) => value?.transferingQty === '-' || value?.foodName === 'No Data');
      const barcodesInWrongStatus = tableData?.filter((value) => value?.status === 'Not Eligible');
      if (invalidBarcodes.length > 0) {
        setInitiated(false);
        toast.error(`${invalidBarcodes?.length} Invalid Barcodes Identified! Please remove them and try again.`);
      } else if (barcodesInWrongStatus.length > 0) {
        setInitiated(false);
        toast.error(`${barcodesInWrongStatus?.length} Barcodes are in current delivery status.`);
      } else if ([...new Set(supplierIds)]?.length > 1) {
        setInitiated(false);
        toast.error(`Only one supplier allowed!`);
      } else {
        dispatch(addStockReturn(data));
      }
    }
  };

  return (
    <Page title='Return - Manager'>
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading='Return - Manager'
          links={[{ name: 'Dashboard', href: PATH_DASHBOARD.root }, { name: 'Stock', href: PATH_DASHBOARD.stocks.transfer.sender }, { name: 'Return - Manager' }]}
          action={[]}
        />
        <Card>
          <Grid container spacing={1}>
            <Grid item xs={12} md={12}>
              <Stack spacing={3} sx={{ p: 3 }}>
                <Card
                  sx={{
                    p: 3,
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: '#eee',
                    },
                  }}
                >
                  <TextField
                    type='text'
                    label={`Enter Barcode`}
                    sx={{ width: '100%' }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.target.select();
                        if (!data?.barcode?.includes(event.target.value)) {
                          playSuccessSound();
                          setData({
                            ...data,
                            barcode: [].concat(data.barcode, [event.target.value]),
                          });
                        }
                      }
                    }}
                  />
                  <br />
                  <br />

                  <DataGridTable
                    name={'Entered Items'}
                    data={tableData}
                    column={columns}
                    isLoading={false}
                    rowSelection={rowSelection}
                    setRowSelection={setRowSelection}
                    isRowClickable={true}
                    enableRowSelection={false}
                    keyField={'stockId'}
                    enablePinning={false}
                    enableRowActions={true}
                    maxHeight={`40vh`}
                    rowsPerPage={8}
                    renderRowActionItems={(row) => [
                      <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: '8px' }} key={`view-stock-${row.original.stockId}`}>
                        <IconButton
                          color='primary'
                          onClick={() => {
                            setData({
                              ...data,
                              barcode: data.barcode.filter((barcode) => barcode !== row.original.barCode),
                            });
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>,
                    ]}
                  />

                  <LoadingButton
                    variant='contained'
                    size='large'
                    style={{ width: '100%' }}
                    onClick={() => {
                      initiateTransfer();
                    }}
                  >
                    Initiate Return Note
                  </LoadingButton>
                </Card>
              </Stack>
            </Grid>
          </Grid>
        </Card>
      </Container>
    </Page>
  );
};

export default ReturnManagement;
